import { styled, createGlobalStyle } from 'styled-components'
import {
  PAPER_DIMENSIONS,
  PaperSize,
  Orientation,
  PrintScale,
  MarginSize,
  MARGIN_VALUES,
  WATERMARK
} from './paperConfig'

const getPaperDimensions = (size: PaperSize, orientation: Orientation) => {
  const dim = PAPER_DIMENSIONS[size]
  return {
    width: orientation === 'portrait' ? dim.width : dim.height,
    height: orientation === 'portrait' ? dim.height : dim.width
  }
}

interface PaperSheetProps {
  $paperSize: PaperSize
  $orientation: Orientation
  $scale: PrintScale
  $margin: MarginSize
}

export const PaperSheet = styled.div<PaperSheetProps>`
  ${({ $paperSize, $orientation, $scale, $margin }) => {
    const { width, height } = getPaperDimensions($paperSize, $orientation)
    const factor = 100 / $scale
    const marginVal = MARGIN_VALUES[$margin]
    const printableWidth = width - 2 * marginVal
    return `
      width: ${width * factor}mm;
      min-height: ${height * factor}mm;
      zoom: ${$scale / 100};
      padding: ${marginVal * factor}mm;
      --print-width: ${printableWidth * factor}mm;
      --print-font-sm: 8pt;
      --print-font-base: 9pt;
      --print-font-md: 10.5pt;
    `
  }}
  position: relative;
  display: flow-root;
  box-sizing: border-box;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .ag-layout-auto-height,
  .ag-layout-print {
    .ag-center-cols-container,
    .ag-center-cols-viewport {
      min-height: 0;
    }
  }
`

export const ContentGrid = styled.div<{ $areas: string; $rows: string }>`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-areas: ${({ $areas }) => $areas};
  grid-template-rows: ${({ $rows }) => $rows};
  gap: 4mm;
`

export const GridSection = styled.div`
  position: relative;
  min-width: 0;
`

export const SectionTitle = styled.h4`
  font-size: var(--print-font-md, 10.5pt) !important;
  margin: 0;
`

export const CardFrame = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  height: 100%;
`

export const ErrorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 16px;
  border: 1px dashed #ff4d4f;
  border-radius: 8px;
  background: #fff2f0;
  color: #ff4d4f;
  font-size: var(--print-font-sm, 9pt);
  gap: 4px;
`

interface PrintGlobalStyleProps {
  $paperSize: PaperSize
  $orientation: Orientation
  $margin: MarginSize
  $mode?: 'drawer' | 'print'
  $pageNumbers?: boolean
  $watermark?: boolean
}

export const PrintGlobalStyle = createGlobalStyle<PrintGlobalStyleProps>`
  ${({ $mode, $paperSize, $orientation, $margin, $pageNumbers, $watermark }) =>
    $mode !== 'print' &&
    `
    @page {
      size: ${$paperSize} ${$orientation};
      margin: ${MARGIN_VALUES[$margin]}mm;
      @top-left { content: ''; }
      @top-center { content: ''; }
      @top-right { content: ''; }
      @bottom-left { content: ''; }
      @bottom-center {
        content: ${$pageNumbers ? `counter(page) " / " counter(pages)` : `''`};
        font-size: 8pt;
        font-family: Manrope, sans-serif;
        color: #8c8c8c;
      }
      @bottom-right {
        content: ${$watermark ? `"${WATERMARK.text}"` : `''`};
        font-size: ${WATERMARK.fontSize};
        font-family: ${WATERMARK.fontFamily};
        color: ${WATERMARK.color};
      }
    }
  `}

  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      height: auto !important;
      overflow: visible !important;
      background: #fff !important;
    }

    .print-paper {
      width: var(--print-width) !important;
      padding: 0 !important;
      min-height: auto !important;
      box-shadow: none !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .ag-root-wrapper,
    .ag-body-viewport,
    .ag-center-cols-viewport,
    .ag-center-cols-container {
      overflow: visible !important;
      height: auto !important;
      min-height: 0 !important;
    }

    .ag-row {
      position: relative !important;
      transform: none !important;
      top: auto !important;
    }

    [data-section-type="graph"],
    [data-section-type="pie"],
    [data-section-type="infobox"] {
      break-inside: avoid;
    }

    .page-break-overlay,
    [data-print-ignore],
    .tsqd-parent-container,
    .delivery-subscription-drawer,
    .print-watermark {
      display: none !important;
    }

    ${({ $mode }) =>
      $mode === 'drawer'
        ? `
      #root { display: none !important; }
      .ant-drawer-mask { display: none !important; }
      .ant-drawer-header, .ant-drawer-close { display: none !important; }
      .print-preview-area {
        padding: 0 !important;
        background: transparent !important;
        overflow: visible !important;
      }
      .ant-drawer,
      .ant-drawer-wrap,
      .ant-drawer-wrapper-body,
      .ant-drawer-content-wrapper,
      .ant-drawer-content,
      .ant-drawer-body {
        position: static !important;
        overflow: visible !important;
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        transform: none !important;
      }
    `
        : `
      #root, .ant-app { width: 100% !important; }
    `}
  }


  ${({ $mode }) =>
    $mode !== 'drawer' &&
    `
    body {
      background: #fff !important;
    }
  `}
`
